import React from "react";
import { Box, Flex, Image } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useReducedMotion } from "framer-motion";
import { useLandingCopy, useLandingLanguage } from "../../LandingLanguageContext";
import { ThemedContainer, ThemedDisplayTitle } from "../themed/ThemedLayout";
import { LectureSlideToJungleQuestion } from "./LectureSlideToJungleQuestion";
import { UploadStacked } from "./UploadStacked";
import { HeroAppStoreCallToAction, WEB_APP_CTA_CLASS_NAME } from "../AppStoreCallToAction";

// The monkey rises from below the section's edge and settles with a lean toward the card.
const monkeyPeekIn = keyframes`
  from { transform: translateY(160px) rotate(0deg); }
  to { transform: translateY(0) rotate(4deg); }
`;

// The Moss family's hero content — headline, stacked upload controls, the doctor monkey at the
// headline's left edge, and the lecture slide with the question it became — independent of the
// backdrop each version paints behind it.
export function JungleHeroContent({ onDark }: { onDark: boolean }) {
  const reduceMotion = useReducedMotion();
  const landingCopy = useLandingCopy();
  const language = useLandingLanguage();
  const headlineColor = onDark ? "#F4F8EC" : "#1F3A17";
  // Only English and Spanish retain editorial desktop breaks. CJK layout
  // breaks do not become word spaces when the browser wraps the sentence.
  const isCjkHeadline = /^(?:zh|ja)(?:-|$)/.test(language);
  const headlineLines = language === "en" || language === "es"
    ? landingCopy.hero.headlineWithDesktopLineBreaks.split("\n")
    : [landingCopy.hero.headlineWithDesktopLineBreaks.replace(/\n/g, isCjkHeadline ? "" : " ")];
  return (
    <ThemedContainer position="relative">
      {/* The doctor monkey stands centred on the headline's left edge — half of it outside the
          text column — with its base just below the section's bottom edge. Rises in once on load. */}
      <Box
        aria-hidden="true"
        position="absolute"
        left={{ base: "-50px", md: "-66px" }}
        bottom={{ base: "-82px", lg: "-126px" }}
        w={{ base: "140px", lg: "196px" }}
        pointerEvents="none"
        transformOrigin="bottom center"
        transform="rotate(4deg)"
        animation={reduceMotion ? "none" : `${monkeyPeekIn} 0.9s cubic-bezier(0.2, 0.9, 0.3, 1.15) 0.6s both`}
      >
        <Image src="/images/landing/inventory/monkey-doctor-nobg.svg" alt="" w="100%" filter={onDark ? "drop-shadow(0 12px 18px rgba(0,0,0,0.35))" : "drop-shadow(0 10px 16px rgba(20,40,16,0.22))"} />
      </Box>
      <Flex direction={{ base: "column", lg: "row" }} align={{ base: "stretch", lg: "center" }} gap={{ base: "40px", lg: "92px" }}>
        <Box flex="1" minW={0} w="100%">
          <ThemedDisplayTitle level="hero" lineHeight={isCjkHeadline ? 1.15 : 1.02} mb={{ base: "28px", lg: "44px" }} color={headlineColor} fontSize={{ base: "40px", md: "58px", lg: "clamp(50px, 5.2vw, 66px)" }} textShadow={onDark ? "0 2px 30px rgba(0,0,0,0.35)" : "none"}>
            {headlineLines.map((headlineLine, headlineLineIndex) => {
              const isLastHeadlineLine = headlineLineIndex === headlineLines.length - 1;
              return (
                <React.Fragment key={headlineLine}>
                  {headlineLine}
                  {/* Below desktop the break is hidden, so the space is what separates the two lines' words. */}
                  {!isLastHeadlineLine && (
                    <>
                      {" "}
                      <Box as="br" display={{ base: "none", lg: "inline" }} />
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </ThemedDisplayTitle>
          {/* Phone visitors from a Meta ad see the store link instead (hidden, not removed, for hydration). */}
          <UploadStacked className={WEB_APP_CTA_CLASS_NAME} maxW="520px" onDark={onDark} />
          <HeroAppStoreCallToAction maxW="520px" onDark={onDark} />
        </Box>

        <Box flex="1" minW={0} w="100%" maxW={{ base: "560px", lg: "none" }} position="relative">
          <LectureSlideToJungleQuestion onLight={!onDark} />
        </Box>
      </Flex>
    </ThemedContainer>
  );
}
